import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, UserCog, ShieldCheck, GitBranch } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Spinner, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, errMsg } from "../../lib/api";

// Phase 71 — Municipal Admin manages staff roles from Master Data.
// Access levels per module are configured on the Role Access page.

const ROLE_TYPE_HINTS = {
  tax_collector: "Field agent — signs into the mobile app, gets territory-based property assignments",
  supervisor: "Office staff — signs into the desktop supervisor portal",
  municipal_admin: "Admin console — signs into the full desktop admin portal",
};
const REPORTS_TO_LABELS = { supervisor: "Supervisor", municipal_admin: "Municipal Admin" };

const emptyForm = { name: "", description: "", base_role: "tax_collector", reports_to: "" };

export const StaffRolesSection = ({ code, readOnly = false }) => {
  const [data, setData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => api.get(`/t/${code}/config/roles`).then((r) => setData(r.data)), [code]);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const allowedReportsTo = (base) => (data?.reports_to_allowed?.[base] || []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, description: r.description || "", base_role: r.base_role,
      reports_to: r.reports_to || "" });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: form.name, description: form.description, base_role: form.base_role,
        reports_to: form.reports_to || null };
      if (editing) {
        await api.put(`/t/${code}/config/roles/${editing.id}`, body);
        toast.success(`Role "${form.name}" updated`);
      } else {
        await api.post(`/t/${code}/config/roles`, body);
        toast.success(`Role "${form.name}" created — it now appears in the Add User form. Fine-tune its access on the Role Access page.`);
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (r) => {
    try {
      await api.put(`/t/${code}/config/roles/${r.id}`, {
        name: r.name, description: r.description, base_role: r.base_role,
        reports_to: r.reports_to, is_active: !(r.is_active !== false),
      });
      toast.success(r.is_active !== false
        ? `"${r.name}" deactivated — hidden from the Add User form (existing staff keep working)`
        : `"${r.name}" activated`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/t/${code}/config/roles/${deleteTarget.id}`);
      toast.success(`Role "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
      setDeleteTarget(null);
    }
  };

  if (!data) return <Spinner />;
  const roles = data.custom_roles || [];
  const baseLabel = (key) => (data.base_roles || []).find((b) => b.key === key)?.label || key;

  return (
    <div data-testid="staff-roles-section">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <p className="font-medium flex items-center gap-2"><UserCog className="h-4 w-4" /> Staff Roles</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Create the roles your staff hold — they appear instantly in the Add User form.
            Module access per role is configured on the{" "}
            <Link to={`/${code}/admin/role-access`} className="text-primary underline underline-offset-2"
              data-testid="staff-roles-role-access-link">Role Access</Link> page.
          </p>
        </div>
        {!readOnly && <Button onClick={openCreate} data-testid="staff-role-add-button">
          <Plus className="h-4 w-4 mr-2" /> Add Role
        </Button>}
      </div>

      {roles.length === 0 ? (
        <EmptyState title="No custom roles yet"
          subtitle="Built-in roles (Municipal Admin, Supervisor, Tax Collector) are always available. Add roles like 'Ward Inspector' or 'Data Entry Clerk' here." />
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <Table data-testid="staff-roles-table">
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Portal</TableHead>
                <TableHead>Reports To</TableHead>
                <TableHead className="text-right">Modules</TableHead>
                <TableHead className="text-right">Staff</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((r) => (
                <TableRow key={r.id} className={r.is_active === false ? "opacity-60" : ""}
                  data-testid={`staff-role-row-${r.id}`}>
                  <TableCell>
                    <p className="font-medium text-sm">{r.name}</p>
                    {r.description && <p className="text-[11px] text-muted-foreground">{r.description}</p>}
                  </TableCell>
                  <TableCell className="text-sm">{baseLabel(r.base_role)}</TableCell>
                  <TableCell className="text-sm">
                    {r.reports_to ? (
                      <span className="flex items-center gap-1">
                        <GitBranch className="h-3 w-3 text-muted-foreground" /> {REPORTS_TO_LABELS[r.reports_to]}
                      </span>
                    ) : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className="text-[10px]">
                      <ShieldCheck className="h-2.5 w-2.5 mr-1" />
                      {Object.values(r.modules || {}).filter((v) => v !== "none").length} enabled
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">{r.user_count}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={r.is_active !== false} onCheckedChange={() => toggleActive(r)} disabled={readOnly}
                        data-testid={`staff-role-active-switch-${r.id}`} />
                      <span className="text-xs text-muted-foreground">{r.is_active !== false ? "Active" : "Inactive"}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {!readOnly && <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(r)}
                        data-testid={`staff-role-edit-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(r)} data-testid={`staff-role-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? `Edit role — ${editing.name}` : "New staff role"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1">
              <Label>Role name <span className="text-destructive">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Ward Inspector" required data-testid="staff-role-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What does this role do? (optional)" data-testid="staff-role-description-input" />
            </div>
            <div className="space-y-1">
              <Label>Role type <span className="text-destructive">*</span></Label>
              <Select value={form.base_role}
                onValueChange={(v) => setForm({ ...form, base_role: v, reports_to: "" })}
                disabled={!!editing && editing.user_count > 0}>
                <SelectTrigger data-testid="staff-role-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tax_collector">Field Agent (Mobile app)</SelectItem>
                  <SelectItem value="supervisor">Office Role (Supervisor console)</SelectItem>
                  <SelectItem value="municipal_admin">Admin Console</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">{ROLE_TYPE_HINTS[form.base_role]}</p>
            </div>
            {allowedReportsTo(form.base_role).length > 0 && (
              <div className="space-y-1">
                <Label className="flex items-center gap-1.5">
                  <GitBranch className="h-3.5 w-3.5" /> Reports to
                </Label>
                <Select value={form.reports_to || allowedReportsTo(form.base_role)[0]}
                  onValueChange={(v) => setForm({ ...form, reports_to: v })}>
                  <SelectTrigger data-testid="staff-role-reports-to-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allowedReportsTo(form.base_role).map((rt) => (
                      <SelectItem key={rt} value={rt}>{REPORTS_TO_LABELS[rt]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Staff holding this role will be mapped under a {REPORTS_TO_LABELS[form.reports_to || allowedReportsTo(form.base_role)[0]]} when created.
                </p>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground bg-accent/50 rounded-md px-2 py-1.5">
              New roles start with full access to their portal — restrict individual modules on the Role Access page.
            </p>
            <Button type="submit" className="w-full" disabled={busy} data-testid="staff-role-submit-button">
              {busy ? "Saving..." : editing ? "Save changes" : "Create role"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Deletion is blocked while any staff member holds this role —
              deactivate it instead to simply hide it from the Add User form.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="staff-role-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="staff-role-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
